import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { AuthState } from '@ngxs-store/auth-store/auth.state';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { VersionService } from '@core/services/version.service';
import { DecodedToken } from '@core/models';

@Component({
  selector: 'app-deployment-banner',
  templateUrl: './deployment-banner.component.html',
  styleUrls: ['./deployment-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeploymentBannerComponent implements OnInit, OnDestroy {
  @Select(AuthState.getDecodedToken) decodedToken$: Observable<DecodedToken>;
  @Select(AuthState.getHasValidToken) hasValidToken$: Observable<boolean>;

  showBanner$: Observable<boolean>;
  private subscriptions: Subscription = new Subscription();

  private readonly DEPLOYMENT_BANNER_KEY = 'deploymentBannerDismissed';
  private readonly LAST_VERSION_KEY = 'lastKnownVersion';

  constructor(
    private versionService: VersionService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.showBanner$ = this.versionService.getCurrentVersion().pipe(
      map(currentVersion => this.shouldShowBanner(currentVersion))
    );

    this.subscriptions.add(
      this.versionService.getCurrentVersion().subscribe(version => {
        this.checkForNewDeployment(version);
      })
    );

    this.subscriptions.add(
      this.hasValidToken$.subscribe(hasValidToken => {
        if (hasValidToken) {
          this.checkForFirstLoginAfterDeployment();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private shouldShowBanner(currentVersion: string): boolean {
    const dismissedVersion = localStorage.getItem(this.DEPLOYMENT_BANNER_KEY);
    const lastKnownVersion = localStorage.getItem(this.LAST_VERSION_KEY);

    if (dismissedVersion === currentVersion) {
      return false;
    }

    return lastKnownVersion !== null && lastKnownVersion !== currentVersion;
  }

  private checkForNewDeployment(currentVersion: string): void {
    const lastKnownVersion = localStorage.getItem(this.LAST_VERSION_KEY);

    if (!lastKnownVersion) {
      localStorage.setItem(this.LAST_VERSION_KEY, currentVersion);
    } else if (lastKnownVersion !== currentVersion) {

      localStorage.removeItem(this.DEPLOYMENT_BANNER_KEY);
      localStorage.setItem(this.LAST_VERSION_KEY, currentVersion);
    }
  }

  private checkForFirstLoginAfterDeployment(): void {
    const currentVersion = localStorage.getItem(this.LAST_VERSION_KEY);
    const lastLoginVersion = localStorage.getItem('lastLoginVersion');

    if (lastLoginVersion !== currentVersion) {

      localStorage.setItem('lastLoginVersion', currentVersion || '');

    }
  }

  dismissBanner(): void {
    this.versionService.getCurrentVersion().pipe(
      take(1)
    ).subscribe(currentVersion => {
      localStorage.setItem(this.DEPLOYMENT_BANNER_KEY, currentVersion);

      this.showBanner$ = this.versionService.getCurrentVersion().pipe(
        map(version => this.shouldShowBanner(version))
      );
    });
  }
}
